import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  PieChart as PieChartIcon, 
  TrendingUp, 
  TrendingDown, 
  Globe, 
  DollarSign, 
  Users, 
  ArrowUpRight, 
  ArrowDownRight,
  Filter,
  Download,
  Calendar,
  Layers,
  ChevronDown
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

// Örnek veriler
const marketShareData = [
  { name: 'Your Brand', share: 32, revenue: 3200000, color: '#3b82f6' },
  { name: 'Competitor A', share: 28, revenue: 2800000, color: '#f43f5e' },
  { name: 'Competitor B', share: 21, revenue: 2100000, color: '#10b981' },
  { name: 'Competitor C', share: 12, revenue: 1200000, color: '#f59e0b' },
  { name: 'Others', share: 7, revenue: 700000, color: '#8b5cf6' },
];

const trendData = [
  { month: 'Jan', yourBrand: 25, competitorA: 30, competitorB: 20, competitorC: 15 },
  { month: 'Feb', yourBrand: 26, competitorA: 29, competitorB: 21, competitorC: 14 },
  { month: 'Mar', yourBrand: 28, competitorA: 28, competitorB: 22, competitorC: 14 },
  { month: 'Apr', yourBrand: 29, competitorA: 28, competitorB: 21, competitorC: 13 },
  { month: 'May', yourBrand: 30, competitorA: 27, competitorB: 20, competitorC: 13 },
  { month: 'Jun', yourBrand: 31, competitorA: 27, competitorB: 20, competitorC: 12 },
  { month: 'Jul', yourBrand: 32, competitorA: 28, competitorB: 21, competitorC: 12 },
];

const geographicData = [
  { region: 'North America', yourShare: 35, totalMarket: 42, growth: 5 },
  { region: 'Europe', yourShare: 28, totalMarket: 35, growth: 2 },
  { region: 'Asia Pacific', yourShare: 32, totalMarket: 38, growth: 8 },
  { region: 'Latin America', yourShare: 25, totalMarket: 32, growth: 4 },
  { region: 'Middle East & Africa', yourShare: 22, totalMarket: 28, growth: 6 },
];

const segmentData = [
  { name: 'Premium Segment', yourShare: 45, share: 45, color: '#3b82f6' },
  { name: 'Mid-range Segment', yourShare: 32, share: 32, color: '#10b981' },
  { name: 'Budget Segment', yourShare: 18, share: 18, color: '#f59e0b' },
  { name: 'Luxury Segment', yourShare: 5, share: 5, color: '#8b5cf6' },
];

const customerInsight = [
  { segment: 'New Customers', value: 38, prevValue: 32, trend: 'up' },
  { segment: 'Repeat Customers', value: 62, prevValue: 58, trend: 'up' },
  { segment: 'Customer Acquisition Cost', value: '$52', prevValue: '$58', trend: 'down' },
  { segment: 'Customer Lifetime Value', value: '$840', prevValue: '$790', trend: 'up' },
];

const MarketShareDashboard = () => {
  const [timeRange, setTimeRange] = useState('quarter');
  const [region, setRegion] = useState('all');
  const [loading, setLoading] = useState(true);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  useEffect(() => {
    // Veri yükleme simülasyonu
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Örnek veri formatlama - zaman aralığına göre
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const ExportMenu = () => (
    <div className="relative">
      <Button 
        className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
        onClick={() => setExportMenuOpen(!exportMenuOpen)}
      >
        <Download size={16} />
        <span>Export</span>
        <ChevronDown size={16} />
      </Button>
      
      {exportMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle PDF export
                setExportMenuOpen(false);
              }}
            >
              Export as PDF
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle Excel export
                setExportMenuOpen(false);
              }}
            >
              Export as Excel
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle CSV export
                setExportMenuOpen(false);
              }}
            >
              Export as CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Market Share Analysis</h1>
          <p className="text-gray-500 mt-1">Compare your brand's performance against competitors in the market</p>
        </div>
        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
            <button 
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${timeRange === 'quarter' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} 
              onClick={() => setTimeRange('quarter')}
            >
              Quarter
            </button>
            <button 
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${timeRange === 'ytd' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} 
              onClick={() => setTimeRange('ytd')}
            >
              YTD
            </button>
            <button 
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${timeRange === 'annual' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} 
              onClick={() => setTimeRange('annual')}
            >
              Annual
            </button>
          </div>
          <Button className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50">
            <Filter size={16} />
            <span>Filters</span>
          </Button>
          <ExportMenu />
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <QuickStatCard 
          title="Your Market Share" 
          value="32%" 
          trend="+2.8%" 
          trendUp={true} 
          icon={<PieChartIcon size={20} />} 
          color="blue" 
          subValue="vs last period"
        />

        <QuickStatCard 
          title="Market Position" 
          value="#1" 
          trend="Improved from #2" 
          trendUp={true} 
          icon={<BarChart size={20} />} 
          color="rose" 
        />

        <QuickStatCard 
          title="Market Growth Rate" 
          value="+8.3%" 
          trend="-0.5%" 
          trendUp={false} 
          icon={<TrendingUp size={20} />} 
          color="emerald" 
          subValue="vs industry"
        />

        <QuickStatCard 
          title="Revenue Share" 
          value={formatCurrency(marketShareData[0].revenue)} 
          trend="+5.2%" 
          trendUp={true} 
          icon={<DollarSign size={20} />} 
          color="amber" 
          subValue="vs last period"
        />
      </div>

      {/* Market Share Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 overflow-hidden border border-gray-200 rounded-xl shadow-sm">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-semibold text-lg text-gray-800">Market Share Trend</h2>
              <div className="flex items-center gap-2">
                <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3">
                  <Calendar size={14} className="mr-1" />
                  Last 6 Months
                </Button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(value) => `${value}%`} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Market Share']}
                  labelFormatter={(label) => `Month: ${label}`}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', border: 'none' }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="yourBrand"
                  name="Your Brand"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  type="monotone"
                  dataKey="competitorA"
                  name="Competitor A"
                  stroke="#f43f5e"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="competitorB"
                  name="Competitor B"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="competitorC"
                  name="Competitor C"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-semibold text-lg text-gray-800">Current Market Distribution</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={marketShareData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="share"
                  nameKey="name"
                  label={({ name, share }) => `${name}: ${share}%`}
                  labelLine={false}
                >
                  {marketShareData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Market Share']} 
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', border: 'none' }}
                />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Geographic Market Share */}
      <Card className="mb-6 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold text-lg text-gray-800">Geographic Market Share Analysis</h2>
            <Select
              className="w-48 h-9 rounded-lg border-gray-200"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            >
              <option value="all">All Regions</option>
              <option value="na">North America</option>
              <option value="eu">Europe</option>
              <option value="apac">Asia Pacific</option>
              <option value="latam">Latin America</option>
              <option value="mea">Middle East & Africa</option>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Market Share</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Market Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">YoY Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {geographicData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Globe className="w-5 h-5 text-gray-400 mr-3" />
                        <div className="font-medium text-gray-900">{item.region}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{item.yourShare}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${item.yourShare}%` }}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{item.totalMarket}%</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.growth > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.growth > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {item.growth > 0 ? '+' : ''}{item.growth}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.yourShare > 30 ? 'bg-green-100 text-green-800' : 
                        item.yourShare > 25 ? 'bg-blue-100 text-blue-800' : 
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.yourShare > 30 ? 'Leading' : item.yourShare > 25 ? 'Competitive' : 'Developing'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Market Segments & Customer Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-semibold text-lg text-gray-800">Market Segments</h2>
              <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3">
                <Layers size={14} className="mr-1" />
                View All Segments
              </Button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={segmentData} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(value) => `${value}%`} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Market Share']}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', border: 'none' }}
                />
                <Bar dataKey="share" name="Your Market Share" radius={[4, 4, 0, 0]}>
                  {segmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-semibold text-lg text-gray-800">Customer Insights</h2>
              <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3">
                <Users size={14} className="mr-1" />
                View Details
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {customerInsight.map((item, index) => (
                <KpiCard
                  key={index}
                  title={item.segment}
                  value={item.value}
                  trend={
                    typeof item.prevValue === 'string' 
                      ? item.trend === 'up' 
                        ? `+${parseInt(item.value.replace('$', '')) - parseInt(item.prevValue.replace('$', ''))}$` 
                        : `-${parseInt(item.prevValue.replace('$', '')) - parseInt(item.value.replace('$', ''))}$`
                      : `${item.trend === 'up' ? '+' : '-'}${Math.abs(item.value - item.prevValue)}%`
                  }
                  trendUp={item.trend === 'up'}
                />
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Competitor Analysis */}
      <Card className="mb-6 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold text-lg text-gray-800">Competitive Landscape</h2>
            <Button className="bg-blue-500 text-white hover:bg-blue-600 text-sm">
              Detailed Competitor Analysis
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border-collapse">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competitor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market Share</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">YoY Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strengths</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Threat Level</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {marketShareData.slice(1, 4).map((competitor, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{competitor.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{competitor.share}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="h-1.5 rounded-full" style={{ width: `${competitor.share}%`, backgroundColor: competitor.color }}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatCurrency(competitor.revenue)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${index === 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {index === 0 ? <TrendingDown className="w-3 h-3 mr-1" /> : <TrendingUp className="w-3 h-3 mr-1" />}
                        {index === 0 ? '-1.2%' : `+${(index + 1) * 0.8}%`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {index === 0 ? 'Price Competitiveness, Marketing' : 
                      index === 1 ? 'Product Innovation, Customer Service' : 
                      'Global Reach, Brand Loyalty'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        index === 0 ? 'bg-red-100 text-red-800' : 
                        index === 1 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {index === 0 ? 'High' : index === 1 ? 'Medium' : 'Low'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Recommendations Section */}
      <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-xl mr-4">
              <TrendingUp size={20} />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-gray-800">Strategic Recommendations</h2>
              <p className="text-gray-500 text-sm">Based on your current market position and competitive landscape</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-100 rounded-xl bg-blue-50">
              <h3 className="font-medium text-blue-700 mb-2">Market Expansion</h3>
              <p className="text-gray-600 text-sm">Focus on expanding in Asia Pacific region where growth rate exceeds 8% and your current share is strong at 32%.</p>
            </div>
            
            <div className="p-4 border border-gray-100 rounded-xl bg-emerald-50">
              <h3 className="font-medium text-emerald-700 mb-2">Product Positioning</h3>
              <p className="text-gray-600 text-sm">Strengthen your premium segment offerings to maintain the 45% market share advantage you currently hold.</p>
            </div>
            
            <div className="p-4 border border-gray-100 rounded-xl bg-amber-50">
              <h3 className="font-medium text-amber-700 mb-2">Competitive Strategy</h3>
              <p className="text-gray-600 text-sm">Monitor Competitor A closely as their price competitiveness poses the highest threat to your market position.</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Helper Components from Overview2
function QuickStatCard({ title, value, trend, trendUp, icon, color, subValue }) {
  const bgColorClass = `bg-${color}-100`;
  const textColorClass = `text-${color}-600`;

  return (
    <Card className="p-5 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <h3 className="text-3xl font-bold mt-2 text-gray-800">{value}</h3>
          <div className={`flex items-center mt-2 ${trendUp ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trendUp ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
            <span className="text-sm font-medium ml-1">{trend} {subValue && <span className="text-gray-500">{subValue}</span>}</span>
          </div>
        </div>
        <div className={`flex items-center justify-center w-12 h-12 ${bgColorClass} ${textColorClass} rounded-xl`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

function KpiCard({ title, value, trend, trendUp }) {
  return (
    <div className="bg-gray-50 p-4 rounded-xl">
      <div className="text-sm text-gray-500 mb-1">{title}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className={`flex items-center mt-2 text-sm ${trendUp ? 'text-emerald-600' : 'text-rose-600'}`}>
        {trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        <span className="ml-1 font-medium">{trend}</span>
      </div>
    </div>
  );
}

export default MarketShareDashboard; 